import { Link } from 'react-router-dom';
import SEOHead from '../../components/feature/SEOHead';

export default function TestPrepPage() {
  const testPrepCourses = [
    {
      id: 'act-math',
      title: 'ACT Math',
      description: 'Complete ACT Math curriculum covering all 6 domains: Pre-Algebra, Elementary Algebra, Intermediate Algebra, Coordinate Geometry, Plane Geometry, and Trigonometry.',
      status: 'available',
      link: '/test-prep/act/math',
      color: 'violet',
      icon: 'ri-calculator-line',
      topics: 6,
      questions: 600
    },
    {
      id: 'act-english',
      title: 'ACT English',
      description: 'Master grammar, punctuation, sentence structure, rhetorical skills, and writing strategies for the ACT English test.',
      status: 'available',
      link: '/test-prep/act/english',
      color: 'blue',
      icon: 'ri-book-open-line',
      topics: 5,
      questions: 200
    },
    {
      id: 'act-science',
      title: 'ACT Science',
      description: 'Develop data interpretation, scientific investigation, and evaluation of models skills for the ACT Science test.',
      status: 'available',
      link: '/test-prep/act/science',
      color: 'green',
      icon: 'ri-flask-line',
      topics: 3,
      questions: 170
    },
    {
      id: 'sat-math',
      title: 'SAT Math',
      description: 'Comprehensive SAT Math preparation covering algebra, problem solving, advanced math, and data analysis.',
      status: 'coming-soon',
      link: '#',
      color: 'orange',
      icon: 'ri-function-line',
      topics: 4,
      questions: 500
    },
    {
      id: 'sat-reading-writing',
      title: 'SAT Reading & Writing',
      description: 'Build critical reading comprehension and writing skills for the SAT Evidence-Based Reading and Writing section.',
      status: 'coming-soon',
      link: '#',
      color: 'pink',
      icon: 'ri-quill-pen-line',
      topics: 4,
      questions: 450
    },
    {
      id: 'state-tests',
      title: 'State Test Prep',
      description: 'Targeted preparation for state standardized tests including STAAR, FSA, PARCC, and more.',
      status: 'coming-soon',
      link: '#',
      color: 'indigo',
      icon: 'ri-map-pin-line',
      topics: 8,
      questions: 800
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="ACT & SAT Test Prep Courses | AP Calculus, AP Statistics | Math Mastery"
        description="Comprehensive test prep for ACT Math, ACT English, ACT Science, SAT, AP Calculus AB, AP Calculus BC, and AP Statistics. 3,250+ practice questions with expert step-by-step solutions."
        keywords="ACT test prep, SAT math prep, AP Calculus prep, AP Statistics prep, ACT math practice, standardized test preparation"
        canonicalPath="/test-prep"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Test Prep Courses',
          description: 'Comprehensive standardized test preparation courses for ACT, SAT, and AP exams.',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'ACT Math Prep', url: '/test-prep/act/math' },
            { '@type': 'ListItem', position: 2, name: 'ACT English Prep', url: '/test-prep/act/english' },
            { '@type': 'ListItem', position: 3, name: 'ACT Science Prep', url: '/test-prep/act/science' },
          ],
        }}
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 text-white py-24">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-trophy-line text-yellow-300"></i>
              <span>Ace Your Standardized Tests</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Test Prep That Gets Results
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
              Comprehensive test preparation courses designed to boost your scores on ACT, SAT, and state standardized tests. Practice with real questions and expert strategies.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/test-prep/act/math"
                className="inline-flex items-center gap-2 bg-white text-violet-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-violet-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                <span>Start ACT Math</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
              <a
                href="#courses"
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap"
              >
                <span>View All Courses</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-violet-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-violet-700 mb-2">3,250+</div>
              <div className="text-gray-600 font-medium">Practice Questions</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-700 mb-2">31</div>
              <div className="text-gray-600 font-medium">Topic Modules</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-indigo-700 mb-2">100%</div>
              <div className="text-gray-600 font-medium">Aligned to Tests</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-violet-700 mb-2">24/7</div>
              <div className="text-gray-600 font-medium">Access Anytime</div>
            </div>
          </div>
        </div>
      </section>

      {/* Test Prep Courses */}
      <section id="courses" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Choose Your Test Prep Course
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Select from our comprehensive test preparation courses. Each course includes detailed lessons, practice questions, and proven strategies.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testPrepCourses.map((course) => (
              <div
                key={course.id}
                className={`bg-white rounded-2xl border-2 ${
                  course.status === 'available'
                    ? 'border-gray-200 hover:border-violet-400 hover:shadow-xl'
                    : 'border-gray-200'
                } transition-all duration-300 overflow-hidden ${
                  course.status === 'available' ? 'cursor-pointer' : 'opacity-75'
                }`}
              >
                {course.status === 'available' ? (
                  <Link to={course.link} className="block">
                    <div className={`bg-gradient-to-br ${
                      course.color === 'violet' ? 'from-violet-500 to-violet-600' :
                      course.color === 'green' ? 'from-emerald-500 to-teal-600' :
                      'from-gray-500 to-gray-600'
                    } p-8 text-white`}>
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-4">
                        <i className={`${course.icon} text-3xl`}></i>
                      </div>
                      <h3 className="text-2xl font-bold mb-2">{course.title}</h3>
                      <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium">
                        <i className="ri-check-line"></i>
                        <span>Available Now</span>
                      </div>
                    </div>
                    <div className="p-6">
                      <p className="text-gray-600 mb-6 leading-relaxed">
                        {course.description}
                      </p>
                      <div className="flex items-center gap-6 mb-6 text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <i className="ri-book-2-line"></i>
                          <span>{course.topics} Topics</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <i className="ri-question-line"></i>
                          <span>{course.questions}+ Questions</span>
                        </div>
                      </div>
                      <div className={`inline-flex items-center gap-2 ${
                        course.color === 'violet' ? 'text-violet-700' :
                        course.color === 'green' ? 'text-emerald-700' :
                        'text-gray-700'
                      } font-semibold whitespace-nowrap`}>
                        <span>Start Learning</span>
                        <i className="ri-arrow-right-line"></i>
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div>
                    <div className={`bg-gradient-to-br from-gray-400 to-gray-500 p-8 text-white`}>
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-4">
                        <i className={`${course.icon} text-3xl`}></i>
                      </div>
                      <h3 className="text-2xl font-bold mb-2">{course.title}</h3>
                      <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium">
                        <i className="ri-time-line"></i>
                        <span>Coming Soon</span>
                      </div>
                    </div>
                    <div className="p-6">
                      <p className="text-gray-600 mb-6 leading-relaxed">
                        {course.description}
                      </p>
                      <div className="flex items-center gap-6 mb-6 text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <i className="ri-book-2-line"></i>
                          <span>{course.topics} Topics</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <i className="ri-question-line"></i>
                          <span>{course.questions}+ Questions</span>
                        </div>
                      </div>
                      <div className="inline-flex items-center gap-2 text-gray-500 font-semibold whitespace-nowrap">
                        <span>Available Soon</span>
                        <i className="ri-lock-line"></i>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Our Test Prep */}
      <section className="py-20 bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Our Test Prep Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Proven strategies and comprehensive practice to maximize your test scores
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-violet-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-focus-3-line text-2xl text-violet-700"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Test-Aligned Content</h3>
              <p className="text-gray-600 leading-relaxed">
                Every lesson and question is carefully aligned to actual test formats and standards.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-lightbulb-line text-2xl text-purple-700"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Strategies</h3>
              <p className="text-gray-600 leading-relaxed">
                Learn proven test-taking strategies and time management techniques from experts.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-question-answer-line text-2xl text-indigo-700"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Detailed Solutions</h3>
              <p className="text-gray-600 leading-relaxed">
                Every practice question includes step-by-step solutions and explanations.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-violet-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-line-chart-line text-2xl text-violet-700"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Track Progress</h3>
              <p className="text-gray-600 leading-relaxed">
                Monitor your improvement with detailed progress tracking and performance analytics.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-file-list-3-line text-2xl text-purple-700"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Practice Tests</h3>
              <p className="text-gray-600 leading-relaxed">
                Full-length practice tests simulate the real testing experience and timing.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-download-line text-2xl text-indigo-700"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Downloadable Resources</h3>
              <p className="text-gray-600 leading-relaxed">
                Access printable worksheets, formula sheets, and study guides anytime.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-violet-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Boost Your Test Scores?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Start with our ACT Math course today and see immediate improvement in your understanding and confidence.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/test-prep/act/math"
              className="inline-flex items-center gap-2 bg-white text-violet-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-violet-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              <span>Start ACT Math Now</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/worksheets"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap"
            >
              <span>Browse Free Worksheets</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}